import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardList, Plus, ChevronRight, X, Send, Loader2 } from "lucide-react";
import { FORMS } from "@/constants/testIds";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

function FormField({ field, value, onChange }) {
  if (field.type === "textarea") return (
    <div>
      <Label>{field.label}{field.required && " *"}</Label>
      <Textarea rows={3} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={field.placeholder} required={field.required} />
    </div>
  );
  if (field.type === "select") return (
    <div>
      <Label>{field.label}{field.required && " *"}</Label>
      <Select value={value || ""} onValueChange={onChange} required={field.required}>
        <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
        <SelectContent>{field.options?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  return (
    <div>
      <Label>{field.label}{field.required && " *"}</Label>
      <Input type={field.type || "text"} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={field.placeholder} required={field.required} />
    </div>
  );
}

function FormModal({ form, onClose }) {
  const { user } = useAuth();
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (name, val) => setValues(v => ({...v, [name]: val}));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await ax().post(`${API}/form-submissions`, { form_id: form.item_id, data: values });
      setDone(true);
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  if (done) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md text-center">
        <div className="py-6">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Send className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="font-bold font-heading text-slate-900 text-lg mb-2">Formulário Enviado!</h3>
          <p className="text-slate-500 text-sm">Sua solicitação foi registrada com sucesso.</p>
          <Button onClick={onClose} className="mt-4" style={{ backgroundColor: "#2346ff" }} data-testid="form-done-btn">Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#2346ff]" />
            {form.title}
          </DialogTitle>
          {form.content && <p className="text-sm text-slate-500">{form.content}</p>}
        </DialogHeader>
        <form data-testid="dynamic-form" onSubmit={submit} className="space-y-3">
          {form.fields?.map(field => (
            <FormField key={field.name} field={field} value={values[field.name]} onChange={v => setField(field.name, v)} />
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" data-testid={FORMS.submitBtn} disabled={submitting} style={{ backgroundColor: "#2346ff" }} className="text-white">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4 mr-2" /> Enviar Solicitação</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const catColors = { "RH": "bg-blue-50 text-blue-600", "Marketing": "bg-purple-50 text-purple-600", "Suprimentos": "bg-orange-50 text-orange-600", "default": "bg-slate-50 text-slate-600" };

export default function FormsPage() {
  const { hasRole, user } = useAuth();
  const [forms, setForms] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSubs, setShowSubs] = useState(false);

  const load = async () => {
    try {
      const [fr, sr] = await Promise.all([
        ax().get(`${API}/forms`),
        ax().get(`${API}/form-submissions/my`)
      ]);
      setForms(fr.data);
      setMySubmissions(sr.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = forms.filter(f =>
    f.title?.toLowerCase().includes(search.toLowerCase()) ||
    f.category?.toLowerCase().includes(search.toLowerCase())
  );

  const byCategory = filtered.reduce((acc, f) => {
    const cat = f.category || "Outros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-[#2346ff]" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Formulários Internos</h1>
            <p className="text-sm text-slate-500">{forms.length} formulários disponíveis</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setShowSubs(!showSubs)} data-testid={FORMS.mySubmissions} className="gap-2 text-sm">
          Minhas Solicitações
          {mySubmissions.length > 0 && <Badge className="bg-[#2346ff] text-white text-xs">{mySubmissions.length}</Badge>}
        </Button>
      </div>

      {showSubs && (
        <div className="mb-6 bento-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Minhas Solicitações</h3>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowSubs(false)}><X className="w-4 h-4" /></Button>
          </div>
          {mySubmissions.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-4">Nenhuma solicitação enviada</p>
          ) : (
            <div className="space-y-2">
              {mySubmissions.map(s => (
                <div key={s.submission_id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{s.form_id}</p>
                    <p className="text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString("pt-BR")}</p>
                  </div>
                  <Badge className={s.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>
                    {s.status === "pending" ? "Pendente" : "Processado"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="relative mb-5">
        <ClipboardList className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Buscar formulários..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div data-testid={FORMS.formList} className="space-y-6">
          {Object.entries(byCategory).map(([cat, catForms]) => (
            <div key={cat}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{cat}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {catForms.map(f => (
                  <div key={f.item_id} data-testid={FORMS.formItem}
                    className="bento-card p-4 cursor-pointer group hover:border-[#2346ff] transition-all"
                    onClick={() => setSelectedForm(f)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${catColors[cat] || catColors.default}`}>{cat}</span>
                        </div>
                        <h3 className="font-semibold text-slate-800 group-hover:text-[#2346ff] transition-colors text-sm">{f.title}</h3>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">{f.content}</p>
                        <p className="text-xs text-slate-400 mt-1">{f.fields?.length || 0} campos</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:text-[#2346ff] transition-colors mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum formulário encontrado</p>
            </div>
          )}
        </div>
      )}

      {selectedForm && <FormModal form={selectedForm} onClose={() => setSelectedForm(null)} />}
    </div>
  );
}
